import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ImageOff, Star } from 'lucide-react';
import { getImageUrl } from '@/utils/api';

const ProductCard = ({ product, index }) => {
  const rawImageUrl = product.image || (product.images && product.images[0]);
  const imageUrl = getImageUrl(rawImageUrl);
  const hasValidImage = imageUrl && !imageUrl.includes('tshirt-ecommerce.emergent.host');
  const [imageError, setImageError] = useState(false);

  // Simple star display (5 stars)
  const StarDisplay = ({ rating = 5 }) => (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-3 h-3 ${
            star <= rating ? 'fill-primary text-primary' : 'fill-zinc-700 text-zinc-700'
          }`}
        />
      ))}
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`}>
        <div className="group relative aspect-[3/4] overflow-hidden border border-white/10 bg-zinc-900/50 hover:border-primary/50 transition-all duration-300 product-card-hover">
          {/* Product Image */}
          {hasValidImage && !imageError ? (
            <img
              src={imageUrl}
              alt={product.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              onError={() => setImageError(true)}
              data-testid="product-card-image"
            />
          ) : (
            <div 
              className="w-full h-full flex flex-col items-center justify-center bg-zinc-800"
              data-testid="product-card-placeholder"
            >
              <ImageOff className="w-16 h-16 text-zinc-600 mb-2" />
              <span className="text-zinc-500 text-sm">Sem imagem</span>
            </div>
          )}

          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />

          {/* Product Info */}
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <h3 className="font-heading text-lg font-bold text-white mb-1 group-hover:text-primary transition-colors" data-testid="product-card-name">
              {product.name}
            </h3>
            <div className="flex items-center gap-2 mb-2">
              <StarDisplay rating={5} />
              <span className="text-xs text-zinc-400">(5.0)</span>
            </div>
            <p className="font-mono text-xl text-primary font-bold" data-testid="product-card-price">
              R$ {product.price.toFixed(2)}
            </p>
            {product.featured && (
              <span className="inline-block mt-2 px-3 py-1 bg-primary text-black text-xs font-bold uppercase tracking-wider" data-testid="featured-badge">
                Destaque
              </span>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ProductCard;